import React, { useMemo } from "react";
import PropTypes from "prop-types";
import WaveChart from "pages/Dashboard/WaveChart";
import formatCurrency from "constants/formatCurrency";

const MonthlyParkerStatistic = ({
    orders,
    startDate,
    endDate,
    granularity,
    locationName,
    acceptedOrderStatuses,
    acceptedPaymentTypes,
    mode
}) => {
    const { labels, series, total, totalAmount } = useMemo(() => {
        if (!orders || !orders.length) return { labels: [], series: [], total: 0, totalAmount: 0 };

        // filter orders
        const filteredOrders = orders.filter((o) => {
            const d = new Date(o.date_created || o.created_at);
            if (isNaN(d.getTime())) return false;

            const orderDateStr = `${d.getFullYear()}-${String(
                d.getMonth() + 1
            ).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            if (startDate && orderDateStr < startDate) return false;
            if (endDate && orderDateStr > endDate) return false;

            if (locationName && locationName !== "All Locations") {
                if ((o.locationName || "").toLowerCase() !== locationName.toLowerCase())
                    return false;
            }

            if (acceptedOrderStatuses && acceptedOrderStatuses.length > 0) {
                if (!acceptedOrderStatuses.includes(String(o.status).toLowerCase()))
                    return false;
            }

            if (acceptedPaymentTypes && acceptedPaymentTypes.length > 0) {
                const pt = o.payment_type?.toLowerCase() || "";
                const match = acceptedPaymentTypes.some((pf) => {
                    if (pf === "stripe" || pf === "qbo") return pt.startsWith(pf);
                    return pt === pf;
                });
                if (!match) return false;
            }

            return true;
        });

        const buckets = new Map();
        let totalAmount = 0;

        filteredOrders.forEach((o) => {
            const d = new Date(o.date_created || o.created_at);
            let key, label;

            if (granularity === "Daily") {
                key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
                    2,
                    "0"
                )}-${String(d.getDate()).padStart(2, "0")}`;
                label = key;
            } else if (granularity === "Weekly") {
                const temp = new Date(d);
                const day = temp.getDay();
                const diffToMonday = temp.getDate() - day + (day === 0 ? -6 : 1);
                const monday = new Date(temp.setDate(diffToMonday));
                key = `${monday.getFullYear()}-W${getWeekNumber(monday)}`;
                label = `W${getWeekNumber(monday)} ${monday.getFullYear()}`;
            } else {
                key = `${d.getFullYear()}-${d.getMonth()}`;
                label = d.toLocaleString("default", { month: "long", year: "numeric" });
            }

            if (!buckets.has(key)) {
                buckets.set(key, { label, customers: new Set(), orders: 0 });
            }

            const bucket = buckets.get(key);

            if (mode === "parker") {
                const customerKey =
                    (o.customer_email || "").toLowerCase() ||
                    `${(o.customer_first_name + " " + o.customer_last_name) || ""}-${o.customer_phone || ""}`;
                bucket.customers.add(customerKey);
            } else {
                bucket.orders += 1;
                totalAmount += o.total || 0;
            }
        });

        const sortedBuckets = Array.from(buckets.values());

        return {
            labels: sortedBuckets.map((b) => b.label),
            series: sortedBuckets.map((b) =>
                mode === "parker" ? b.customers.size : b.orders
            ),
            total: sortedBuckets.reduce(
                (sum, b) => sum + (mode === "parker" ? b.customers.size : b.orders),
                0
            ),
            totalAmount,
        };
    }, [
        orders,
        startDate,
        endDate,
        granularity,
        locationName,
        acceptedOrderStatuses,
        acceptedPaymentTypes,
        mode,
    ]);

    return (
        <div className="d-flex justify-content-center mt-4">
            <WaveChart
                title={`Number of ${mode === "parker" ? "Parkers" : "Orders"
                    } (${granularity}) (Total: ${total}${mode === "order" ? ` - $${formatCurrency(totalAmount)}` : ""})`}
                labels={labels}
                series={series}
                statName={mode === "parker" ? "Parkers" : "Orders"}
            />
        </div>
    );
};

// helper
function getWeekNumber(d) {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

MonthlyParkerStatistic.propTypes = {
    orders: PropTypes.array.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    granularity: PropTypes.oneOf(["Daily", "Weekly", "Monthly"]),
    locationName: PropTypes.string,
    acceptedOrderStatuses: PropTypes.arrayOf(PropTypes.string),
    acceptedPaymentTypes: PropTypes.arrayOf(PropTypes.string),
    mode: PropTypes.oneOf(["order", "parker"]),
};

MonthlyParkerStatistic.defaultProps = {
    granularity: "Monthly",
    locationName: "All Locations",
    acceptedOrderStatuses: [],
    acceptedPaymentTypes: [],
    mode: "order",
};

export default MonthlyParkerStatistic;